import React, { useEffect, useState } from 'react'
import AdminHOC from '../../../components/shared/HOC/AdminHOC';
import { message, Modal } from "antd";
import { UsergroupAddOutlined } from "@ant-design/icons";
import { AdminDomainColumns } from '../../../assets/FormFeilds/AdminDomainColumns';
import useDomain from '../../../hooks/AdminHooks/useAdmindomain';
import DomainModal from './DomainModal';
import CustomTable from '../../../components/shared/table/Table.jsx';
import CustomTab from '../../../components/shared/tab/TabComponent.jsx';
import CustomButton from '../../../components/global/Button/CustomButton.jsx';
import { Row, Col } from 'antd';
import CustomInput from '../../../components/global/CustomInput/CustomInput.jsx';
import LottieIcon from '../../../components/shared/lottieIcon/LottieIcon.jsx';
import searchIconsvg from "../../../assets/Search-Icon.svg";
import ConfirmDeletePopup from '../../../components/shared/confirmDeletePopup/ConfirmDeletePopup.jsx';
import { useSelector } from 'react-redux';

const DomainPage = () => {
    const auth = useSelector((state) => state.auth);
    const isAdminSupport = auth.roles.includes("ADMIN_SUPPORT");

    const { domains, fetchDomains, deleteDomain } = useDomain();

    const [tableState, setTableState] = useState({
        loading: false,
        totalElements: 0,
        currentPage: 1,
        pageSize: 10,
    });

    const [filterState, setFilterState] = useState({
        searchText: '',
        activeTab: '1',
        isInactive: false,
        isInputHovered: false,
    });

    const [modalState, setModalState] = useState({
        isDomainModalOpen: false,
        selectedDomain: null,
        isDeleteModalOpen: false,
        domainToDelete: null,
    });

    const tabList = [
        { key: "1", label: "Active Domains" },
        { key: "2", label: "Inactive Domains" },
    ];

    const fetchAllDomains = async (page, size, query) => {
        setTableState(prev => ({ ...prev, loading: true }));
        try {
            const response = await fetchDomains(page, size, query, !filterState.isInactive);
            setTableState(prev => ({
                ...prev,
                totalElements: response ? response.data.totalElements : 0,
            }));
            if (!response) message.warning('No domains found');
        } catch (error) {
            console.error("Error loading domains:", error);
            setTableState(prev => ({ ...prev, totalElements: 0 }));
        } finally {
            setTableState(prev => ({ ...prev, loading: false }));
        }
    };

    const filteredDomains = domains?.filter(item =>
        filterState.isInactive ? item.isActive === false : item.isActive === true
    ) || [];

    const handleOpenAddModal = () =>
        setModalState(prev => ({ ...prev, isDomainModalOpen: true, selectedDomain: null }));

    const handleEditUser = (record) =>
        setModalState(prev => ({ ...prev, isDomainModalOpen: true, selectedDomain: record }));

    const handleDelete = (record) =>
        setModalState(prev => ({ ...prev, isDeleteModalOpen: true, domainToDelete: record }));

    const confirmDelete = async () => {
        if (!modalState.domainToDelete) return;
        setTableState(prev => ({ ...prev, loading: true }));
        const success = await deleteDomain(modalState.domainToDelete.id);
        if (success) {
            await fetchAllDomains(tableState.currentPage, tableState.pageSize, filterState.searchText);
            setModalState(prev => ({ ...prev, isDeleteModalOpen: false, domainToDelete: null }));
        }
        setTableState(prev => ({ ...prev, loading: false }));
    };

    const handleSearch = (value) => {
        setFilterState(prev => ({ ...prev, searchText: value }));
        setTableState(prev => ({ ...prev, currentPage: 1 }));
    };

    const handleTabChange = (key) => {
        setFilterState(prev => ({ ...prev, activeTab: key, isInactive: key === "2",searchText: '' }));
        setTableState(prev => ({ ...prev, currentPage: 1 }));
    };

    const handleTableChange = (pagination) => {
        if (!pagination) return;
        setTableState(prev => ({
            ...prev,
            currentPage: pagination.pageSize !== prev.pageSize ? 1 : pagination.current,
            pageSize: pagination.pageSize,
        }));
    };

    useEffect(() => {
        const query = filterState.searchText?.trim();
        if (query.length > 0 && query.length < 2) return;
        const timer = setTimeout(() => {
            fetchAllDomains(tableState.currentPage, tableState.pageSize, query);
        }, 1000);
        return () => clearTimeout(timer);
    }, [tableState.currentPage, tableState.pageSize, filterState.searchText, filterState.isInactive]);

    const columns = AdminDomainColumns(handleEditUser, handleDelete, filterState.isInactive, isAdminSupport );

    return (
        <div className="p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)] gap-6">
            <div className="flex flex-col gap-6">
                <h1 className="text-[#e74860] text-[1.75rem] font-normal">Domains</h1>

                <div>
                    <Row>
                        <Col span={7}>
                            <CustomTab
                                entries={tabList}
                                activeKey={filterState.activeTab}
                                onChange={handleTabChange}
                            />
                        </Col>

                        <Col span={17}>
                            <div className="flex gap-[10px] justify-end">
                                <div
                                    onMouseEnter={() => setFilterState(prev => ({ ...prev, isInputHovered: true }))}
                                    onMouseLeave={() => setFilterState(prev => ({ ...prev, isInputHovered: false }))}
                                >
                                    <CustomInput
                                        value={filterState.searchText}
                                        icon={
                                            filterState.isInputHovered ? (
                                                <LottieIcon name="searchIcon" size={24} shouldPlay={true} />
                                            ) : (
                                                <img src={searchIconsvg} alt="search" className="w-6 h-6" />
                                            )
                                        }
                                        className="!w-[250px]"
                                        onChange={(e) => handleSearch(e.target.value)}
                                        placeholder="Search domain"
                                        allowClear
                                    />
                                </div>

                                {(!filterState.isInactive && auth.roles.includes("ADMIN")) && (
                                    <CustomButton
                                        onClick={handleOpenAddModal}
                                        variant="primary"
                                    >
                                        Add Domain
                                    </CustomButton>
                                )}
                            </div>
                        </Col>

                        <Row className="w-full">
                            <div className="w-full bg-white rounded-[8px] shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
                                <CustomTable
                                    data={filteredDomains}
                                    columns={columns}
                                    showLoader={tableState.loading}
                                    className={"top-table-radius"}
                                    pagination={{
                                        current: tableState.currentPage,
                                        pageSize: tableState.pageSize,
                                        total: tableState.totalElements,
                                        showSizeChanger: true,
                                    }}
                                    onChange={handleTableChange}
                                    scroll={{ y: "calc(100vh - 400px)" }}
                                    serverSide={true}
                                />
                            </div>
                        </Row>
                    </Row>
                </div>
            </div>

            <DomainModal
                isModalOpen={modalState.isDomainModalOpen}
                handleCloseModal={() => setModalState(prev => ({ ...prev, isDomainModalOpen: false }))}
                getDomains={() => fetchAllDomains(tableState.currentPage, tableState.pageSize, filterState.searchText)}
                selectedDomain={modalState.selectedDomain}
                setLoading={(val) => setTableState(prev => ({ ...prev, loading: val }))}
                loading={tableState.loading}
            />

            <ConfirmDeletePopup
                isOpen={modalState.isDeleteModalOpen}
                onClose={() => setModalState(prev => ({ ...prev, isDeleteModalOpen: false }))}
                onConfirm={confirmDelete}
                title="Confirm Deletion"
                text={`Are you sure you want to delete ${modalState.domainToDelete?.domain}? This action cannot be undone.`}
                confirmtext="Delete"
            />
        </div>
    );
};

export default AdminHOC(DomainPage);